import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Descriptions,
  Tag,
  Button,
  message,
  Row,
  Col,
  Alert,
  Timeline,
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

function PaymentDetails({
  visible,
  paymentId,
  onClose,
  onPaymentUpdate,
  selectedPaymentDetails,
}) {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [modal, contextHolder] = Modal.useModal();

  // API hooks
  const [detailsResponse, getPaymentDetails] = useApi();
  const [authorizeResponse, authorizePayment] = useApi();

  useEffect(() => {
    if (visible && paymentId) {
      // Since there's no specific payment details endpoint, we'll get from payments list
      // Or you can create a specific endpoint
      // getPaymentDetails(
      //   null,
      //   API_METHODS.GET,
      //   `/admin/payments/all?payment_id=${paymentId}`
      // );
      setPaymentDetails(selectedPaymentDetails);
    }
  }, [visible, paymentId]);

  useEffect(() => {
    if (detailsResponse.data && !detailsResponse.isLoading) {
      // Assuming the API returns array, get first item
      const payment = Array.isArray(detailsResponse.data)
        ? detailsResponse.data.find((p) => p.id == paymentId)
        : detailsResponse.data;
      setPaymentDetails(payment);
    }
  }, [detailsResponse, paymentId]);

  useEffect(() => {
    if (authorizeResponse.data && !authorizeResponse.isLoading) {
      message.success("Payment authorized successfully");
      onPaymentUpdate && onPaymentUpdate();
    }
  }, [authorizeResponse]);

  const handleAuthorizePayment = () => {
    modal.confirm({
      title: "Authorize Payment",
      content: (
        <div>
          <p>Are you sure you want to manually authorize this payment?</p>
          <Alert
            message="Important"
            description="Please verify this payment in your Razorpay dashboard before authorizing. This action will confirm the order and clear the customer's cart."
            type="warning"
            showIcon
            style={{ marginTop: "12px" }}
          />
        </div>
      ),
      onOk: () => {
        authorizePayment(
          { admin_confirmed: true },
          API_METHODS.POST,
          `/admin/payments/authorize/${paymentId}`
        );
      },
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  if (!paymentDetails) {
    return (
      <Modal
        destroyOnHidden={true}
        title="Payment Details"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
        loading={detailsResponse.isLoading}
      >
        Loading payment details...
      </Modal>
    );
  }

  const needsAuthorization =
    !paymentDetails.frontend_verified && !paymentDetails.webhook_verified;

  // Create payment timeline
  const getPaymentTimeline = () => {
    const timeline = [];

    timeline.push({
      color: "blue",
      icon: <CreditCardOutlined />,
      children: (
        <div>
          <div style={{ fontWeight: "500" }}>Payment Initiated</div>
          <div style={{ color: "#666", fontSize: "12px" }}>
            {new Date(paymentDetails.created_on).toLocaleString("en-IN")}
          </div>
        </div>
      ),
    });

    if (paymentDetails.payment_status === "completed") {
      timeline.push({
        color: "green",
        icon: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Payment Completed</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Payment processed successfully
            </div>
          </div>
        ),
      });
    }

    if (paymentDetails.frontend_verified) {
      timeline.push({
        color: "cyan",
        icon: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Frontend Verified</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Payment verified on frontend
            </div>
          </div>
        ),
      });
    }

    if (paymentDetails.webhook_verified) {
      timeline.push({
        color: "purple",
        icon: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Webhook Verified</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Payment verified via webhook
            </div>
          </div>
        ),
      });
    }

    return timeline;
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <DollarOutlined style={{ marginRight: "8px", color: "#f97316" }} />
          Payment Details -{" "}
          {paymentDetails.razorpay_payment_id || paymentDetails.id}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="razorpay"
          type="default"
          onClick={() =>
            window.open(
              `https://dashboard.razorpay.com/app/payments/${paymentDetails.razorpay_payment_id}`,
              "_blank"
            )
          }
        >
          View in Razorpay
        </Button>,
        needsAuthorization && (
          <Button
            key="authorize"
            type="primary"
            icon={<SafetyOutlined />}
            onClick={handleAuthorizePayment}
            loading={authorizeResponse.isLoading}
            style={{ backgroundColor: "#fa8c16", borderColor: "#fa8c16" }}
          >
            Authorize Payment
          </Button>
        ),
      ]}
    >
      {contextHolder}
      {/* Alert for authorization needed */}
      {needsAuthorization && (
        <Alert
          message="Authorization Required"
          description="This payment needs manual authorization. Please verify the payment in Razorpay dashboard before authorizing."
          type="warning"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      <Row gutter={16}>
        {/* Payment Information */}
        <Col xs={24} lg={12}>
          <Card
            title="Payment Information"
            size="small"
            style={{ marginBottom: "16px" }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Payment ID">
                <Tag color="blue" style={{ fontFamily: "monospace" }}>
                  {paymentDetails.id}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Razorpay Payment ID">
                <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {paymentDetails.razorpay_payment_id || "N/A"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Razorpay Order ID">
                <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {paymentDetails.razorpay_order_id || "N/A"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#f97316",
                  }}
                >
                  {formatCurrency(paymentDetails.amount)}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Currency">
                {paymentDetails.currency || "INR"}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                <Tag
                  color={
                    paymentDetails.payment_status === "completed"
                      ? "green"
                      : paymentDetails.payment_status === "failed"
                      ? "red"
                      : paymentDetails.payment_status === "refunded"
                      ? "purple"
                      : "orange"
                  }
                >
                  {paymentDetails.payment_status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {paymentDetails.payment_method || "Online"}
              </Descriptions.Item>
              <Descriptions.Item label="Created On">
                {new Date(paymentDetails.payment_date).toLocaleString("en-IN")}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Order Information */}
          <Card title="Related Order" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Order Number">
                <Tag color="cyan">{paymentDetails.order_number}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Order Amount">
                {formatCurrency(
                  paymentDetails.order_amount || paymentDetails.amount
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                <div>
                  <div style={{ fontWeight: "500" }}>
                    {paymentDetails.user_name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {paymentDetails.email}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Verification Status & Timeline */}
        <Col xs={24} lg={12}>
          <Card
            title="Verification Status"
            size="small"
            style={{ marginBottom: "16px" }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ textAlign: "center", padding: "16px" }}>
                  {paymentDetails.frontend_verified ? (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "24px",
                        color: "#52c41a",
                        marginBottom: "8px",
                      }}
                    />
                  ) : (
                    <ExclamationCircleOutlined
                      style={{
                        fontSize: "24px",
                        color: "#fa8c16",
                        marginBottom: "8px",
                      }}
                    />
                  )}
                  <div style={{ fontWeight: "500" }}>Frontend</div>
                  <Tag
                    color={paymentDetails.frontend_verified ? "green" : "red"}
                  >
                    {paymentDetails.frontend_verified
                      ? "VERIFIED"
                      : "NOT VERIFIED"}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: "center", padding: "16px" }}>
                  {paymentDetails.webhook_verified ? (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "24px",
                        color: "#52c41a",
                        marginBottom: "8px",
                      }}
                    />
                  ) : (
                    <ExclamationCircleOutlined
                      style={{
                        fontSize: "24px",
                        color: "#fa8c16",
                        marginBottom: "8px",
                      }}
                    />
                  )}
                  <div style={{ fontWeight: "500" }}>Webhook</div>
                  <Tag
                    color={paymentDetails.webhook_verified ? "green" : "red"}
                  >
                    {paymentDetails.webhook_verified
                      ? "VERIFIED"
                      : "NOT VERIFIED"}
                  </Tag>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Payment Timeline */}
          <Card title="Payment Timeline" size="small">
            <Timeline items={getPaymentTimeline()} />
          </Card>
        </Col>
      </Row>

      {/* Gateway Response (if available) */}
      {paymentDetails.payment_gateway_response && (
        <Card
          title="Gateway Response"
          size="small"
          style={{ marginTop: "16px" }}
        >
          <pre
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "4px",
              fontSize: "12px",
              maxHeight: "200px",
              overflow: "auto",
            }}
          >
            {JSON.stringify(paymentDetails.payment_gateway_response, null, 2)}
          </pre>
        </Card>
      )}
    </Modal>
  );
}
export default PaymentDetails;
